/**
 * Playlist CRUD modal flows — TASK 9.3 Step 3 + 4.
 *
 * The mock's `im` input modal (`_createPlaylist` / `_renamePlaylist` L930-933),
 * its delete confirm (`pl.del` L1080) and its duplicate-add confirm
 * (`_addToPlaylist` L927), rebuilt on the app's real overlay plumbing. These
 * functions own FLOW only: the markup is ../../app/overlays.js's `InputModal`/
 * `ConfirmModal`, and order/dismissal/focus belong to ../../app/OverlayStack.js
 * — going through `overlays.open()` is what makes every one of these
 * focus-trapped, Escape-dismissable and hardware-back-dismissable without a
 * single line here (AC 9.1 / the §10.1 mock-defect fixes).
 *
 * ── NAME VALIDATION (AC 8.2) ──────────────────────────────────────────────
 * {@link playlistNameError} is the client half of `dto.ts#playlistNameSchema`:
 * NFC-normalize, trim, then count CODE POINTS — `[...name].length`, never
 * `name.length` — because the server (and the DB CHECK it mirrors) counts code
 * points, and a UTF-16 count would reject a legal 60-emoji name. Inline errors
 * ride `InputModal`'s own error-string seam (`onSubmit` returns the message).
 *
 * ── SERVER ERRORS RENDER INLINE, IN THE OPEN MODAL ────────────────────────
 * Every cap in this family is a 422 `VALIDATION` with no dedicated code — the
 * duplicate name, the 500-playlist cap, the 5,000-track cap — distinguishable
 * only by `fieldErrors` (api-client.ts#fieldErrorText). `error.message` on
 * those is the useless generic "Some fields are invalid.", so the modal
 * surfaces `fieldErrorText(error)` in its own inline `role="alert"` slot and
 * STAYS OPEN with the user's text intact, rather than toasting and dismissing.
 *
 * ── "ADD ANYWAY" IS A FRESH IDEMPOTENCY KEY, NOT A FLAG (AC 9.2) ──────────
 * The add contract has no `allowDuplicate` field. The server appends whatever
 * it is asked to (the same track may legitimately appear twice); replaying the
 * SAME `idempotencyKey` is absorbed as a retry and adds nothing, a NEW key is
 * a deliberate duplicate insert. So {@link addToPlaylist} mints one key per
 * user decision: the plain add mints its own, and confirming "Add anyway"
 * mints ANOTHER — which is the entire wire-level difference between "I already
 * asked for this" and "yes, twice on purpose".
 *
 * @module
 */

/* global crypto -- browser platform API (randomUUID); eslint.config.js's
   browser globals block predates the views using it and is outside TASK 9.3's
   file set */

import { ERROR_CODES, PLAYLIST_NAME_MAX_CHARS, normalizePlaylistName } from '@streamymusic/shared';

import { fieldErrorText } from '../../../api/api-client.js';
import { ConfirmModal, InputModal } from '../../app/overlays.js';

/** @typedef {import('../../app/OverlayStack.js').OverlayStackHandle} OverlayStackHandle */
/** @typedef {import('../../app/OverlayStack.js').OverlayDescriptor} OverlayDescriptor */
/** @typedef {import('@streamymusic/shared').PlaylistSummary} PlaylistSummary */

/** ui-contract §10.5 — the empty-submit message, verbatim from the mock. */
export const NAME_REQUIRED_MESSAGE = 'Name is required (1–100 characters).';
/** mock `_imSubmit` — the over-long branch. */
export const NAME_TOO_LONG_MESSAGE = 'Name must be 100 characters or fewer.';
/** mock `_addToPlaylist` L927 — the duplicate-add confirm title. */
export const ALREADY_IN_PLAYLIST_TITLE = 'Already in playlist';

/**
 * ui-contract §10.5 — the client-side duplicate message, with the typed name.
 *
 * @param {string} name
 * @returns {string}
 */
export function duplicateNameMessage(name) {
  return `You already have a playlist called “${name}”.`;
}

/**
 * Client-side validation of a playlist name — the inline half of AC 8.2.
 *
 * Counts CODE POINTS after NFC-normalize + trim (`[...s].length`), exactly as
 * `dto.ts#playlistNameSchema` and the DB `char_length` CHECK do, so a name
 * this function accepts is never rejected server-side for length. The
 * duplicate check compares NORMALIZED forms (the server's uniqueness is over
 * the stored NFC form) against whatever names the caller has loaded — it is a
 * best-effort fast path; the server's 422 remains the authority and renders
 * inline through the same seam.
 *
 * @param {string} raw the untouched input value
 * @param {{ existingNames?: readonly string[], currentName?: string|null }} [options]
 *   `currentName` (rename) exempts the playlist's own name — renaming to the
 *   name it already has is a server no-op, not a duplicate.
 * @returns {string|null} the inline error message, or null when the name is valid
 */
export function playlistNameError(raw, options = {}) {
  const { existingNames = [], currentName = null } = options;
  const name = normalizePlaylistName(raw);
  const points = [...name].length;
  if (points < 1) return NAME_REQUIRED_MESSAGE;
  if (points > PLAYLIST_NAME_MAX_CHARS) return NAME_TOO_LONG_MESSAGE;
  if (currentName !== null && name === normalizePlaylistName(currentName)) return null;
  for (const existing of existingNames) {
    if (normalizePlaylistName(existing) === name) return duplicateNameMessage(name);
  }
  return null;
}

/**
 * The safe display text for a failed playlist mutation: the actionable
 * `fieldErrors` entry when there is one (the caps and the duplicate name all
 * live there), else the error's own safe message, else a generic fallback.
 *
 * @param {unknown} error
 * @returns {string}
 */
export function mutationErrorText(error) {
  const field = fieldErrorText(error);
  if (field !== null) return field;
  if (error instanceof Error && error.message !== '') return error.message;
  return 'Something went wrong. Try again.';
}

/**
 * Writes a message into an open `InputModal`'s inline `role="alert"` slot —
 * the SAME node the modal's own sync validation writes to — and returns focus
 * to the field. This is how an ASYNC server rejection (a 422 duplicate name,
 * the 500-playlist cap) lands inline instead of as a toast that dismisses the
 * modal.
 *
 * @param {OverlayDescriptor} descriptor
 * @param {string|null} message null hides the slot (a fresh in-flight submit)
 * @returns {void}
 */
function paintInlineError(descriptor, message) {
  const error = descriptor.element.querySelector('.sm-form-error');
  if (!(error instanceof HTMLElement)) return;
  if (message === null) {
    error.setAttribute('hidden', '');
    error.textContent = '';
    return;
  }
  error.textContent = message;
  error.removeAttribute('hidden');
  const input = descriptor.element.querySelector('input');
  if (input instanceof HTMLElement) input.focus({ preventScroll: true });
}

/**
 * Disables/enables the modal's submit control while a request is in flight,
 * so Enter-mashing cannot double-create.
 *
 * @param {OverlayDescriptor} descriptor
 * @param {boolean} busy
 * @returns {void}
 */
function paintBusy(descriptor, busy) {
  const submit = descriptor.element.querySelector('button[type="submit"]');
  if (!(submit instanceof HTMLElement)) return;
  if (busy) submit.setAttribute('disabled', '');
  else submit.removeAttribute('disabled');
}

/**
 * Shared create/rename plumbing: one `InputModal` whose submit runs the
 * client-side name rules synchronously (inline on failure), then the given
 * server call, closing only on success and painting a server rejection into
 * the same inline slot.
 *
 * @param {{
 *   overlays: OverlayStackHandle,
 *   title: string,
 *   value?: string,
 *   placeholder?: string,
 *   existingNames?: readonly string[],
 *   currentName?: string|null,
 *   submit: (name: string) => Promise<PlaylistSummary>,
 *   onDone: (summary: PlaylistSummary) => void,
 * }} options
 * @returns {OverlayDescriptor}
 */
function openNameModal(options) {
  const {
    overlays,
    title,
    value = '',
    placeholder = '',
    existingNames = [],
    currentName = null,
    submit,
    onDone,
  } = options;

  let busy = false;
  const descriptor = InputModal({
    title,
    label: 'Playlist name',
    value,
    placeholder,
    confirmLabel: 'Save',
    onSubmit: (raw) => {
      if (busy) return null;
      const clientError = playlistNameError(raw, { existingNames, currentName });
      if (clientError !== null) return clientError;

      const name = normalizePlaylistName(raw);
      busy = true;
      paintBusy(descriptor, true);
      paintInlineError(descriptor, null);
      submit(name).then(
        (summary) => {
          // Close THEN act — `closeTop({then})` runs the callback after the
          // history traversal lands, so the caller never races the popstate.
          overlays.closeTop({ then: () => onDone(summary) });
        },
        (error) => {
          busy = false;
          paintBusy(descriptor, false);
          paintInlineError(descriptor, mutationErrorText(error));
        },
      );
      return null;
    },
    onCancel: () => overlays.closeTop(),
  });
  overlays.open(descriptor);
  return descriptor;
}

/**
 * "+ NEW PLAYLIST" → the "New playlist" input modal (AC 9.1 create half).
 *
 * @param {{
 *   overlays: OverlayStackHandle,
 *   api: Pick<import('../../../api/api-client.js').ApiClient, 'createPlaylist'>,
 *   existingNames?: readonly string[],
 *   onCreated?: (summary: PlaylistSummary) => void,
 * }} options
 *   `existingNames` is whatever the caller has LOADED — the fast client check;
 *   the server's 422 covers the rest and renders inline too.
 * @returns {OverlayDescriptor}
 */
export function openCreatePlaylistModal(options) {
  const { overlays, api, existingNames = [], onCreated = () => {} } = options;
  return openNameModal({
    overlays,
    title: 'New playlist',
    placeholder: 'e.g. Rooftop Sessions',
    existingNames,
    submit: (name) => api.createPlaylist(name),
    onDone: onCreated,
  });
}

/**
 * "Rename" → the "Rename playlist" input modal, prefilled (AC 9.1).
 *
 * @param {{
 *   overlays: OverlayStackHandle,
 *   api: Pick<import('../../../api/api-client.js').ApiClient, 'renamePlaylist'>,
 *   playlist: { id: string, name: string },
 *   existingNames?: readonly string[],
 *   onRenamed?: (summary: PlaylistSummary) => void,
 * }} options
 * @returns {OverlayDescriptor}
 */
export function openRenamePlaylistModal(options) {
  const { overlays, api, playlist, existingNames = [], onRenamed = () => {} } = options;
  return openNameModal({
    overlays,
    title: 'Rename playlist',
    value: playlist.name,
    existingNames,
    currentName: playlist.name,
    submit: (name) => api.renamePlaylist(playlist.id, name),
    onDone: onRenamed,
  });
}

/**
 * "Delete" → the AC 9.1 confirm, verbatim mock copy (ui-contract §10.1
 * "Delete playlist confirmation").
 *
 * A 404 from the DELETE is SUCCESS: the Story-6 DELETE contract documents it
 * as "unknown, already-deleted, or another user's" — the playlist is absent,
 * which is exactly what was asked for — so it takes the success path and is
 * never surfaced as an error.
 *
 * @param {{
 *   overlays: OverlayStackHandle,
 *   api: Pick<import('../../../api/api-client.js').ApiClient, 'deletePlaylist'>,
 *   playlist: { id: string, name: string },
 *   onDeleted?: () => void,
 *   onError?: (message: string) => void,
 * }} options
 * @returns {OverlayDescriptor}
 */
export function openDeletePlaylistModal(options) {
  const { overlays, api, playlist, onDeleted = () => {}, onError = () => {} } = options;

  let busy = false;
  const descriptor = ConfirmModal({
    title: 'Delete playlist?',
    body: `“${playlist.name}” will be permanently deleted. This can’t be undone.`,
    confirmLabel: 'Delete',
    confirmBackground: 'var(--sm-color-pink)',
    onConfirm: () => {
      if (busy) return;
      busy = true;
      api.deletePlaylist(playlist.id).then(
        () => overlays.closeTop({ then: () => onDeleted() }),
        (error) => {
          const code = /** @type {{ code?: string }} */ (error).code;
          if (code === ERROR_CODES.NOT_FOUND) {
            // Already absent — success, per the DELETE contract.
            overlays.closeTop({ then: () => onDeleted() });
            return;
          }
          busy = false;
          overlays.closeTop({ then: () => onError(mutationErrorText(error)) });
        },
      );
    },
    onCancel: () => overlays.closeTop(),
  });
  overlays.open(descriptor);
  return descriptor;
}

/**
 * Adds a track (or a whole album, in album order) to a playlist, owning the
 * AC 9.2 duplicate-confirm: a track the caller knows is already in the
 * playlist prompts "Already in playlist … Add it again anyway?" FIRST, and a
 * confirmed re-add goes out with a FRESH `idempotencyKey` — see the module
 * header for why the key IS the "add anyway" signal.
 *
 * Duplicate detection is client-side by contract (the server never asks): the
 * caller answers `isDuplicate` from the membership it has loaded. Albums are
 * never duplicate-prompted — the server appends the album's tracks whatever
 * the playlist holds, and there is no honest per-album answer client-side.
 *
 * Failure surfaces the ACTIONABLE text: `fieldErrorText` for the 422 family
 * (the 5,000-track cap keyed `fieldErrors.trackId`/`.albumId`), the safe
 * message otherwise (`NOT_FOUND`, add-time `GONE`). The server applies caps
 * atomically — a refused add inserted NOTHING (AC 9.4), so `onError` with no
 * `onAdded` is the complete client-side story.
 *
 * @param {{
 *   overlays: OverlayStackHandle,
 *   api: Pick<import('../../../api/api-client.js').ApiClient, 'addPlaylistTracks'>,
 *   playlist: { id: string, name: string },
 *   target: { source: 'track', trackId: string, title: string }
 *         | { source: 'album', albumId: string, title: string },
 *   isDuplicate?: boolean,
 *   newKey?: () => string,
 *   onAdded?: (summary: PlaylistSummary) => void,
 *   onError?: (message: string) => void,
 * }} options
 *   `newKey` mints one idempotency key PER USER DECISION (default
 *   `crypto.randomUUID`); injectable so tests can observe the keys differ.
 * @returns {void}
 */
export function addToPlaylist(options) {
  const {
    overlays,
    api,
    playlist,
    target,
    isDuplicate = false,
    newKey = () => crypto.randomUUID(),
    onAdded = () => {},
    onError = () => {},
  } = options;

  const commit = () => {
    // ONE key per decision: the original add and a confirmed "add anyway" are
    // different decisions, so they mint different keys — that difference is
    // what the server reads as "deliberate duplicate", not a flag.
    const idempotencyKey = newKey();
    const input =
      target.source === 'track'
        ? { source: /** @type {const} */ ('track'), trackId: target.trackId, idempotencyKey }
        : { source: /** @type {const} */ ('album'), albumId: target.albumId, idempotencyKey };
    api.addPlaylistTracks(playlist.id, input).then(
      (summary) => onAdded(summary),
      (error) => onError(mutationErrorText(error)),
    );
  };

  if (target.source === 'track' && isDuplicate) {
    overlays.open(
      ConfirmModal({
        title: ALREADY_IN_PLAYLIST_TITLE,
        body: `“${target.title}” is already in ${playlist.name}. Add it again anyway?`,
        confirmLabel: 'Add anyway',
        confirmBackground: 'var(--sm-color-yellow)',
        onConfirm: () => overlays.closeTop({ then: commit }),
        onCancel: () => overlays.closeTop(),
      }),
    );
    return;
  }

  commit();
}
